import { useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Award, Loader2, AlertCircle } from "lucide-react";
import { trpc } from "@/lib/trpc";
import BadgeCard from "./BadgeCard";

/**
 * BadgeList Component
 * Display all badges with tabs for "All" and "My Badges"
 */
export default function BadgeList() {
  const { t } = useTranslation();
  const { data: allBadges = [], isLoading: loadingAll, error: errorAll, refetch: refetchAll } = trpc.badges.list.useQuery();
  const { data: userBadges = [], isLoading: loadingMy, error: errorMy, refetch: refetchMy } = trpc.badges.my.useQuery();

  // Create a map of awarded badge IDs for quick lookup
  const awardedBadgeIds = useMemo(() => {
    return new Set(userBadges.map((ub: any) => ub.badge_id));
  }, [userBadges]);

  // Create a map of awarded dates
  const awardedDates = useMemo(() => {
    const map = new Map();
    userBadges.forEach((ub: any) => {
      map.set(ub.badge_id, ub.awarded_at);
    });
    return map;
  }, [userBadges]);

  // Merge badges with user's awarded status
  const badgesWithStatus = useMemo(() => {
    return allBadges.map((badge: any) => ({
      ...badge,
      isAwarded: awardedBadgeIds.has(badge.id),
      awardedAt: awardedDates.get(badge.id),
    }));
  }, [allBadges, awardedBadgeIds, awardedDates]);

  const awardedBadges = badgesWithStatus.filter((b) => b.isAwarded);
  const notAwardedBadges = badgesWithStatus.filter((b) => !b.isAwarded);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5" />
          {t('badges.title')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="all">
              {t('badges.all')} ({allBadges.length})
            </TabsTrigger>
            <TabsTrigger value="my">
              {t('badges.my_badges')} ({awardedBadges.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-3 mt-4">
            {loadingAll && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {errorAll && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <span>{t('badges.error_loading')}</span>
                  <Button variant="outline" size="sm" onClick={() => refetchAll()}>
                    {t('common.retry')}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {!loadingAll && !errorAll && allBadges.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                {t('badges.no_badges_available')}
              </div>
            )}

            {!loadingAll && badgesWithStatus.length > 0 && (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {badgesWithStatus.map((badge: any) => (
                  <BadgeCard
                    key={badge.id}
                    title={badge.title}
                    description={badge.description}
                    iconUrl={badge.icon_url}
                    isAwarded={badge.isAwarded}
                    awardedAt={badge.awardedAt}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="my" className="space-y-3 mt-4">
            {loadingMy && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}

            {errorMy && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="flex items-center justify-between">
                  <span>{t('badges.error_loading_my_badges')}</span>
                  <Button variant="outline" size="sm" onClick={() => refetchMy()}>
                    {t('common.retry')}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {!loadingMy && !errorMy && awardedBadges.length === 0 && (
              <div className="text-center text-muted-foreground py-8">
                {t('badges.no_badges_yet')}
                <br />
                {t('badges.complete_modules_to_earn')}
              </div>
            )}

            {!loadingMy && awardedBadges.length > 0 && (
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {awardedBadges.map((badge: any) => (
                  <BadgeCard
                    key={badge.id}
                    title={badge.title}
                    description={badge.description}
                    iconUrl={badge.icon_url}
                    isAwarded={badge.isAwarded}
                    awardedAt={badge.awardedAt}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}

